import { useEffect, useState } from 'react';
import {
  fetchPublicTrustSnapshot,
  type FetchPublicTrustSnapshotParams,
} from '../services/publicTrustService.ts';
import type { PublicTrustSnapshot } from '../types/publicTrust.ts';
import { fireAnalytics } from '../utils/firebase';
import { getDeviceCategory, getSessionId } from '../utils/analyticsHelpers.ts';

export type UseTrustSnapshotOptions = FetchPublicTrustSnapshotParams & {
  enabled?: boolean;
  analyticsSource?: string;
};

export function useTrustSnapshot({
  enabled = true,
  analyticsSource = 'private_project_publish_welcome',
  ...params
}: UseTrustSnapshotOptions = {}) {
  const [snapshot, setSnapshot] = useState<PublicTrustSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const jurisdictionId = params.jurisdictionId;
  const limit = params.limit ?? 4;
  const context = params.context ?? 'private_project_publish';

  useEffect(() => {
    if (!enabled) {
      setSnapshot(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchPublicTrustSnapshot({ context, jurisdictionId, limit }).then((data) => {
      if (cancelled) return;
      setSnapshot(data);
      setIsLoading(false);

      if (data) {
        fireAnalytics('trust_snapshot_loaded', {
          session_id: getSessionId(),
          device_category: getDeviceCategory(),
          source: analyticsSource,
          context: data.context,
          scope: data.meta.scope,
          jurisdiction_id: data.meta.jurisdictionId,
          activity_count: data.activity.length,
          hide_activity: data.meta.hideActivity,
          professionals_in_scope: data.stats.professionalsInScope,
          professionals_verified: data.stats.professionalsVerified,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, context, jurisdictionId, limit, analyticsSource]);

  return {
    snapshot,
    isLoading,
  };
}
